import type { Thunk } from 'types/redux'
import type { NativeTag } from 'types/track'
import api from '@disco/api/server'
import { NATIVE_TAGS_EDIT } from '@disco/constants/ActionTypes'
import { pushUINotification } from '@disco/utils/notifications'
/**
 * Creates a new custom native tag with the specified name and category.
 * @param name - The name for the new tag
 * @param category_id - The ID of the category to add the tag to
 * @returns Thunk that creates the tag and shows success/error notifications
 */
export function createCustomNativeTag(
  name: string,
  category_id: number,
): Thunk {
  return async (dispatch, getState) => {
    try {
      const updatedTag = await api.createCustomNativeTag(name, category_id)
      // optimistic version would be:
      // const updatedTag = { name, category_id, is_default_tag: false }
      dispatch({
        type: NATIVE_TAGS_EDIT,
        id: null,
        tag: updatedTag,
      })
      pushUINotification({
        title: 'New tag added!',
        message: `“${name}” (${
          getState().entities.nativeCategories[category_id].name
        })`,
        level: 'success',
      })
      return updatedTag
    } catch (error) {
      pushUINotification({
        message: error?.body?.detail || 'Saving failed',
        level: 'error',
      })
      throw new Error(error)
    }
  }
}
/**
 * Edits an existing custom native tag with the provided data.
 * @param tagId - The ID of the tag to edit
 * @param data - Partial tag data containing the fields to update
 * @returns Thunk that updates the tag and shows success/error notifications
 */
export function editCustomNativeTag(
  tagId: NativeTag['id'],
  data: Partial<NativeTag>,
): Thunk {
  return async (dispatch, getState) => {
    try {
      const originalTag = getState().entities.nativeTags[tagId]
      const updatedTag = await api.editCustomNativeTag(tagId, data)
      // optimistic version would be:
      // const updatedTag = tag
      dispatch({
        type: NATIVE_TAGS_EDIT,
        id: tagId,
        category_id: originalTag.category_id,
        tag: updatedTag,
      })
      pushUINotification({
        title: 'Tag edited!',
        message: `“${updatedTag.name}” (${
          getState().entities.nativeCategories[updatedTag.category_id].name
        })`,
        level: 'success',
      })
      return updatedTag
    } catch (error) {
      pushUINotification({
        message: error?.body?.detail || 'Saving failed',
        level: 'error',
      })
      throw new Error(error)
    }
  }
}
/**
 * Changes the sharing status of a custom native tag (public/private).
 * @param tagId - The ID of the tag to modify
 * @param isShared - Whether the tag should be shared publicly
 * @returns Thunk that updates the tag sharing status and shows notifications
 */
export function shareCustomNativeTag(
  tagId: NativeTag['id'],
  isShared: boolean,
): Thunk {
  return async (dispatch, getState) => {
    try {
      const updatedTag = await api.editCustomNativeTag(tagId, {
        tag_is_shared: isShared,
      })
      dispatch({
        type: NATIVE_TAGS_EDIT,
        id: tagId,
        category_id: updatedTag.category_id,
        tag: updatedTag,
      })
      pushUINotification({
        title: updatedTag.tag_is_shared
          ? 'Tag changed to public'
          : 'Tag changed to private',
        message: `“${updatedTag.name}” (${
          getState().entities.nativeCategories[updatedTag.category_id].name
        })`,
        level: 'success',
      })
      return updatedTag
    } catch (error) {
      pushUINotification({
        message: error?.body?.detail || 'Saving failed',
        level: 'error',
      })
      throw new Error(error)
    }
  }
}
/**
 * Deletes a custom native tag from the system.
 * @param tag - The tag object to delete
 * @returns Thunk that removes the tag and shows success/error notifications
 */
export function deleteCustomNativeTag(tag: NativeTag): Thunk {
  return async (dispatch, getState) => {
    try {
      await api.deleteCustomNativeTag(tag.id)
      // optimistic version would be: dispatch before request
      dispatch({
        type: NATIVE_TAGS_EDIT,
        id: tag.id,
        category_id: tag.category_id,
        tag: null,
      })
      pushUINotification({
        title: 'Tag removed!',
        message: `“${tag.name}” (${
          getState().entities.nativeCategories[tag.category_id].name
        })`,
        level: 'success',
      })
    } catch (error) {
      pushUINotification({
        message: error?.body?.detail || 'Failed to remove',
        level: 'error',
      })
      throw new Error(error)
    }
  }
}
